import { useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import { useLandingCampaignTopDonors } from '@/hooks/useLandingCampaignTopDonors'
import {
  formatTopDonorAmount,
  topDonorInitial,
  type LandingTopDonor,
} from '@/lib/landing-top-donors'
import { DonorAvatar } from '@/features/donorAvatar/DonorAvatar'
import {
  parseAvatarConfig,
  type AvatarConfig,
} from '@/features/donorAvatar/avatar-catalog'
import { DonorMascot } from '@/features/donorMascot/DonorMascot'
import {
  parseMascotConfig,
  type MascotConfig,
} from '@/features/donorMascot/mascot-catalog'
import styles from './LandingProfileTopDonors.module.css'
import type { LandingCampaign } from '@/types/landing'
import { buildRankingPositionOffers } from './ranking-dispute'
import { RankingDisputeDialog } from './RankingDisputeDialog'
import { TopDonorProfileDialog } from './TopDonorProfileDialog'

type LandingProfileTopDonorsProps = {
  campaignId: number
  refreshKey?: number
  campaign?: LandingCampaign | null
  salesOpen?: boolean
  purchaseLoading?: boolean
  onPurchaseForRanking?: (quantity: number, amountCents: number) => Promise<void>
}

export function LandingProfileTopDonors({
  campaignId,
  refreshKey,
  campaign,
  salesOpen = true,
  purchaseLoading = false,
  onPurchaseForRanking,
}: LandingProfileTopDonorsProps) {
  const { items, viewerTotalAmount, viewerRank, phase, errorMessage } =
    useLandingCampaignTopDonors(campaignId, refreshKey)
  const [disputeOpen, setDisputeOpen] = useState(false)
  const [selectedDonor, setSelectedDonor] = useState<LandingTopDonor | null>(null)
  const [purchaseError, setPurchaseError] = useState('')
  const offers = useMemo(
    () =>
      campaign
        ? buildRankingPositionOffers({
            items,
            viewerTotalAmount,
            viewerRank,
            unitPrice: campaign.price,
            minimumQuantity: campaign.min_quantity_per_purchase,
            maximumQuantity: campaign.max_quantity_per_purchase,
          })
        : null,
    [campaign, items, viewerRank, viewerTotalAmount]
  )

  function assumePosition(quantity: number, amountCents: number) {
    if (!onPurchaseForRanking) return
    setDisputeOpen(false)
    setPurchaseError('')
    void onPurchaseForRanking(quantity, amountCents).catch((error) => {
      setPurchaseError(
        error instanceof Error && error.message === 'widget_ranking_payment_pending'
          ? 'Já existe um Pix em andamento. Conclua ou cancele a compra atual antes de escolher outra posição.'
          : 'Não foi possível abrir o Pix desta posição. Tente novamente.'
      )
    })
  }

  return (
    <section className={styles.board} aria-labelledby="top-donors-heading">
      <header className={styles.header}>
        <p className={styles.kicker}>
          <Icon icon="mdi:trophy-outline" aria-hidden /> Hall da campanha
        </p>
        <h2 className={styles.title} id="top-donors-heading">
          Maiores compradores
        </h2>
        <p className={styles.lead}>
          Quem mais apoia esta campanha. Compre mais e conquiste um lugar aqui.
        </p>
      </header>

      {phase === 'loading' ? (
        <div className={styles.skeleton} aria-busy="true" aria-label="Carregando ranking">
          <p className={styles.status}>Carregando ranking…</p>
          <div className={styles.skeletonBlock} />
        </div>
      ) : null}

      {phase === 'error' ? <p className={styles.status}>{errorMessage}</p> : null}

      {phase === 'ready' && items.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyMark} aria-hidden>
            <Icon icon="mdi:trophy-outline" width={28} />
          </span>
          <p className={styles.emptyTitle}>Seja o primeiro a aparecer neste ranking</p>
          <p className={styles.emptyText}>
            Os 10 maiores compradores desta campanha ganham destaque aqui.
          </p>
        </div>
      ) : null}

      {phase === 'ready' && items.length > 0 ? (
        <ol className={styles.list} aria-label="Maiores compradores da campanha">
          {items.map((item) => (
            <li
              key={item.rank}
              className={`${styles.row} ${item.rank === 1 ? styles.row1 : item.rank === 2 ? styles.row2 : item.rank === 3 ? styles.row3 : ''}`}
              aria-label={`${item.rank}º lugar`}
            >
              <button
                type="button"
                className={styles.rowButton}
                onClick={() => setSelectedDonor(item)}
                aria-label={`Ver perfil de ${item.display_name}, ${item.rank}º lugar`}
              >
                <span className={styles.listRank}>
                  {item.rank <= 3 ? (
                    <Icon
                      icon={item.rank === 1 ? 'mdi:crown' : 'mdi:medal-outline'}
                      aria-hidden
                    />
                  ) : null}
                  {item.rank}º
                </span>
                <TopDonorAvatar
                  photoUrl={item.profile_photo_url}
                  name={item.display_name}
                  avatar={item.avatar}
                  mascot={item.mascot}
                />
                <span className={styles.listName}>{item.display_name}</span>
                <span className={styles.listAmount}>
                  {formatTopDonorAmount(item.total_amount)}
                </span>
              </button>
            </li>
          ))}
        </ol>
      ) : null}

      {phase === 'ready' &&
      offers !== null &&
      campaign?.generation_status !== 'donation_only' &&
      onPurchaseForRanking ? (
        <div className={styles.disputeInvite}>
          <div className={styles.disputeCopy}>
            <span className={styles.disputeIcon}>
              <Icon icon="mdi:trophy-award" aria-hidden />
            </span>
            <span>
              <strong>Sua próxima posição começa aqui</strong>
              <small>Descubra o valor e os números da sorte de cada posição.</small>
            </span>
          </div>
          <button
            type="button"
            className={styles.disputeButton}
            disabled={purchaseLoading}
            onClick={() => setDisputeOpen(true)}
          >
            Entrar na Disputa <Icon icon="mdi:arrow-right" aria-hidden />
          </button>
          {purchaseError ? (
            <p className={styles.disputeError} role="alert">
              {purchaseError}
            </p>
          ) : null}
        </div>
      ) : null}
      {disputeOpen && campaign ? (
        <RankingDisputeDialog
          offers={offers}
          viewerTotalAmount={viewerTotalAmount}
          unitPrice={campaign.price}
          minimumQuantity={campaign.min_quantity_per_purchase}
          maximumQuantity={campaign.max_quantity_per_purchase}
          salesOpen={salesOpen}
          onClose={() => setDisputeOpen(false)}
          onAssume={(offer) => assumePosition(offer.quantity, offer.amountCents)}
        />
      ) : null}
      {selectedDonor ? (
        <TopDonorProfileDialog
          donor={selectedDonor}
          onClose={() => setSelectedDonor(null)}
        />
      ) : null}
    </section>
  )
}

function TopDonorAvatar({
  photoUrl,
  name,
  avatar,
  mascot,
}: {
  photoUrl: string | null
  name: string
  avatar?: AvatarConfig | null
  mascot?: MascotConfig | null
}) {
  const config = parseAvatarConfig(avatar)
  const mascotConfig = parseMascotConfig(mascot)

  return (
    <span className={styles.avatarSlot}>
      {config ? (
        <DonorAvatar
          config={config}
          className={`${styles.avatar} ${styles.avatarIllustration}`}
        />
      ) : photoUrl ? (
        <img
          src={photoUrl}
          alt=""
          className={styles.avatar}
          referrerPolicy="no-referrer"
        />
      ) : (
        <span className={styles.avatarFallback} aria-hidden>
          {topDonorInitial(name)}
        </span>
      )}
      {mascotConfig ? (
        <span className={styles.mascot} title={mascotConfig.name || undefined}>
          <DonorMascot config={mascotConfig} showBackdrop={false} />
        </span>
      ) : null}
    </span>
  )
}
